import React, { useEffect, useState } from "react";
import { Newspaper, Loader2, Copy, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LangPicker } from "@/components/language/LangPicker";

const NEWS_TYPES = [
  { id: "music", label: "Music News" },
  { id: "entertainment", label: "Entertainment" },
  { id: "sports", label: "Sports News" },
  { id: "weather", label: "Weather Update" },
  { id: "traffic", label: "Traffic Report" },
  { id: "breaking", label: "Breaking News" },
];

export const LangNewsTab = ({ overview, stationId }) => {
  const [ntype, setNtype] = useState("music");
  const [stories, setStories] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [langs, setLangs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (overview) setLangs(overview.active_languages || []); }, [overview]);
  useEffect(() => {
    api.get("/news/stories", { params: { limit: 30 } }).then((r) => setStories(r.data || [])).catch(() => {});
  }, []);

  const toggleLang = (c) => setLangs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const generate = async () => {
    if (!langs.length) return toast.error("Pick at least one language");
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/language-center/news/localize", {
        station_id: stationId, news_type: ntype, source_story_id: sourceId, languages: langs });
      setResult(r.data);
      toast.success("Bulletins localized");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Localization failed");
    } finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="lc-news-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">Multilingual News Center</h2>
      <p className="text-sm text-ink-secondary mb-6">Generate one story, localize it instantly into every language</p>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">News Type</div>
            <div className="space-y-1">
              {NEWS_TYPES.map((t) => (
                <button key={t.id} onClick={() => setNtype(t.id)} data-testid={`lc-newstype-${t.id}`}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    ntype === t.id ? "bg-brand-amber/10 border border-brand-amber/40 text-ink-primary font-semibold"
                      : "border border-transparent text-ink-secondary hover:bg-bg-elevated"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <LangPicker all={overview?.languages?.all || []} selected={langs} onToggle={toggleLang} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">Source Story</div>
          <select
            value={sourceId} onChange={(e) => setSourceId(e.target.value)} data-testid="lc-news-source-select"
            className="w-full rounded-xl border border-line bg-bg-surface p-3 text-sm text-ink-primary focus:outline-none focus:border-brand-amber/50"
          >
            <option value="">— Auto-generate fresh news —</option>
            {stories.map((s) => (
              <option key={s.story_id} value={s.story_id}>{(s.headline || "").slice(0, 90)}</option>
            ))}
          </select>
          <p className="text-[11px] text-ink-muted mt-1">Select an existing story from your News Center, or leave blank to generate fresh news.</p>
          <button
            onClick={generate} disabled={busy} data-testid="lc-news-generate-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Localizing…" : `Generate in ${langs.length} Language${langs.length === 1 ? "" : "s"}`}
          </button>

          {!result && !busy && (
            <div className="mt-6 rounded-xl border border-dashed border-line p-10 text-center text-sm text-ink-muted">
              <Newspaper className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Select languages and click Generate to localize news
            </div>
          )}

          {result && (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4" data-testid="lc-news-source-box">
                <div className="font-mono text-[9px] uppercase tracking-wider text-sky-300 mb-1">Source · {result.source_label}</div>
                <div className="whitespace-pre-wrap text-sm text-ink-secondary">{result.source}</div>
              </div>
              {result.items.map((r) => (
                <div key={r.language} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`lc-news-result-${r.language}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-[9px] uppercase rounded bg-bg-elevated px-1.5 py-0.5 text-ink-secondary">{r.language}</span>
                    <span className="text-sm font-semibold text-ink-primary">{r.name}</span>
                    <span className="text-xs text-ink-muted">{r.native}</span>
                    <span className="flex-1" />
                    {!r.error && (
                      <button onClick={() => copy(r.output)} className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] text-ink-secondary hover:text-ink-primary transition">
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                    )}
                  </div>
                  {r.error
                    ? <div className="text-xs text-rose-400">Failed: {r.error}</div>
                    : <div className="whitespace-pre-wrap text-sm text-ink-secondary leading-relaxed">{r.output}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
